import * as THREE from 'three';
import { EventManager } from '../core/EventManager';
import { Logger } from '../core/Logger';
import { MaterialLoadingDebugger } from './MaterialLoadingDebugger';

export interface MaterialLoadingProgress {
    totalMaterials: number;
    loadedMaterials: number;
    failedMaterials: number;
    currentlyLoading: string[];
    progress: number; // 0-100
}

/**
 * MaterialLoadingManager - Tracks loading progress of textures and videos
 * Ensures all materials are loaded before rendering starts
 * 🎯 SINGLETON PATTERN - Use MaterialLoadingManager.Instance
 */
export class MaterialLoadingManager {
    private static instance: MaterialLoadingManager;
    
    private eventManager: EventManager;
    private loadingItems: Map<string, {
        type: 'texture' | 'video';
        url: string;
        isLoaded: boolean;
        hasError: boolean;
        progress: number;
    }> = new Map();
    
    private threeLoadingManager!: THREE.LoadingManager;
    private totalItems: number = 0;
    private loadedItems: number = 0;
    private failedItems: number = 0;
    private itemStartTimes: Map<string, number> = new Map();
    private hangingCheckInterval: NodeJS.Timeout | null = null;
    
    // Promise for waiting until all materials are loaded
    private allLoadedPromise: Promise<void>;
    private allLoadedResolve: ((value: void) => void) | null = null;
    
    private constructor(eventManager: EventManager) {
        this.eventManager = eventManager;
        this.setupThreeLoadingManager();
        
        // Create promise for waiting until all materials are loaded
        this.allLoadedPromise = new Promise<void>((resolve) => {
            this.allLoadedResolve = resolve;
        });
        
        // Setup timeout for hanging materials
        this.setupLoadingTimeout();
        
        // Enable detailed debugging
        MaterialLoadingDebugger.Instance.setEnabled(true);
        
        // Setup global debug functions for browser console
        MaterialLoadingDebugger.setupGlobalDebugFunctions();
    }
    
    /**
     * Get the singleton instance of MaterialLoadingManager
     * Must be initialized first with initialize()
     */
    public static get Instance(): MaterialLoadingManager {
        if (!MaterialLoadingManager.instance) {
            throw new Error('MaterialLoadingManager must be initialized first with MaterialLoadingManager.initialize(eventManager)');
        }
        return MaterialLoadingManager.instance;
    }
    
    /**
     * Initialize the singleton instance
     */
    public static initialize(eventManager: EventManager): MaterialLoadingManager {
        if (MaterialLoadingManager.instance) {
            Logger.Instance.Warn('MaterialLoadingManager already initialized, returning existing instance', 'MaterialLoading');
            return MaterialLoadingManager.instance;
        }
        
        MaterialLoadingManager.instance = new MaterialLoadingManager(eventManager);
        //Logger.Instance.Info('🎯 MaterialLoadingManager singleton initialized', 'MaterialLoading');
        return MaterialLoadingManager.instance;
    }
    
    /**
     * Check if the singleton has been initialized
     */
    public static isInitialized(): boolean {
        return MaterialLoadingManager.instance !== undefined;
    }
    
    private setupLoadingTimeout(): void {
        // Check for hanging materials every 10 seconds (increased from 5s)
        this.hangingCheckInterval = setInterval(() => {
            this.checkForHangingMaterials();
        }, 10000);
    }
    
    private checkForHangingMaterials(): void {
        if (this.totalItems === 0) return;
        
        // Skip check if all materials are already loaded
        if (this.isAllLoaded()) {
            return;
        }
        
        const hangingItems = Array.from(this.loadingItems.values())
            .filter(item => !item.isLoaded && !item.hasError);
        
        if (hangingItems.length > 0) {
            // Only warn about items that have been loading for more than 15 seconds
            const genuinelyStuckItems = hangingItems.filter(item => {
                const loadTime = performance.now() - this.getItemStartTime(item.url);
                return loadTime > 15000; // 15 seconds threshold for "hanging"
            });
            
            if (genuinelyStuckItems.length > 0) {
                Logger.Instance.Warn(
                    `⚠️ ${genuinelyStuckItems.length} materials seem to be hanging (>15s):`,
                    'MaterialLoading'
                );
                
                genuinelyStuckItems.forEach(item => {
                    const loadTime = performance.now() - this.getItemStartTime(item.url);
                    Logger.Instance.Warn(`  - ${item.type}: ${item.url} (${(loadTime / 1000).toFixed(1)}s elapsed)`, 'MaterialLoading');
                });
            }
            
            // Auto-timeout hanging items after 30 seconds (increased from 15s)
            hangingItems.forEach(item => {
                const loadTime = performance.now() - this.getItemStartTime(item.url);
                if (loadTime > 30000) {
                    Logger.Instance.Error(`⏰ Timeout: Marking ${item.url} as failed after ${(loadTime / 1000).toFixed(1)}s`, 'MaterialLoading');
                    item.hasError = true;
                    this.failedItems++;
                    this.updateProgress();
                    this.checkIfAllLoaded();
                } else if (loadTime <= 15000) {
                    // Log progress for debugging only for items under 15s (not stuck yet)
                    //Logger.Instance.Debug(`⏳ Still loading ${item.url} (${(loadTime / 1000).toFixed(1)}s elapsed)`, 'MaterialLoading');
                }
            });
        }
    }
    
    private getItemStartTime(url: string): number {
        return this.itemStartTimes.get(url) || performance.now();
    }
    
    private setupThreeLoadingManager(): void {
        this.threeLoadingManager = new THREE.LoadingManager();
        
        this.threeLoadingManager.onLoad = () => {
            const loadTime = performance.now();
            ////Logger.Instance.Info(`🎨 [TIMING] All THREE.js resources loaded at ${loadTime.toFixed(2)}ms`, 'MaterialLoading');
            
            // 🎯 FIX: Mark any remaining textures as loaded when THREE.js reports all complete
            const unloadedTextures = Array.from(this.loadingItems.values())
                .filter(item => item.type === 'texture' && !item.isLoaded && !item.hasError);
                
            unloadedTextures.forEach(item => {
                //Logger.Instance.Debug(`✅ Auto-marking texture as loaded from THREE.js onLoad: ${item.url}`, 'MaterialLoading');
                this.markTextureLoaded(item.url);
            });
            
            this.checkIfAllLoaded();
        };
        
        this.threeLoadingManager.onProgress = (url: string, loaded: number, total: number) => {
            const progressTime = performance.now();
            ////Logger.Instance.Debug(`📈 [TIMING] THREE.js progress: ${url} - ${loaded}/${total} at ${progressTime.toFixed(2)}ms`, 'MaterialLoading');
            
            // 🔍 DEBUG: Record progress event
            MaterialLoadingDebugger.Instance.recordProgress(url, loaded, total);
            
            const item = this.loadingItems.get(url);
            if (item) {
                item.progress = total > 0 ? (loaded / total) * 100 : 0;
                this.updateProgress();
                
                // 🎯 FIX: Mark texture as loaded when progress reaches 100%
                if (loaded >= total && !item.isLoaded) {
                    this.markTextureLoaded(url);
                }
            }
        };
        
        this.threeLoadingManager.onError = (url: string) => {
            const errorTime = performance.now();
            //Logger.Instance.Error(`❌ [TIMING] Failed to load resource: ${url} at ${errorTime.toFixed(2)}ms`, 'MaterialLoading');
            const item = this.loadingItems.get(url);
            if (item) {
                item.hasError = true;
                this.failedItems++;
                this.updateProgress();
            }
        };
    }
    
    /**
     * Register a texture for loading tracking
     */
    public registerTexture(url: string): void {
        const registerStart = performance.now();
        
        if (this.loadingItems.has(url)) {
            ////Logger.Instance.Debug(`🔄 [TIMING] Texture already registered: ${url}`, 'MaterialLoading');
            return;
        }
        
        // 🔍 DEBUG: Register material with debugger
        MaterialLoadingDebugger.Instance.registerMaterial(url, 'texture');
        
        this.loadingItems.set(url, {
            type: 'texture',
            url,
            isLoaded: false,
            hasError: false,
            progress: 0
        });
        
        // Track start time for timeout detection
        this.itemStartTimes.set(url, performance.now());
        
        this.totalItems++;
        
        const registerEnd = performance.now();
        ////Logger.Instance.Debug(`📋 [TIMING] Registered texture for loading: ${url} in ${(registerEnd - registerStart).toFixed(2)}ms`, 'MaterialLoading');
        this.updateProgress();
    }
    
    /**
     * Register a video for loading tracking
     */
    public registerVideo(url: string, videoElement: HTMLVideoElement): void {
        if (this.loadingItems.has(url)) return;
        
        // 🔍 DEBUG: Register material with debugger
        MaterialLoadingDebugger.Instance.registerMaterial(url, 'video');
        
        this.loadingItems.set(url, {
            type: 'video',
            url,
            isLoaded: false,
            hasError: false,
            progress: 0
        });
        
        // Track start time for timeout detection
        this.itemStartTimes.set(url, performance.now());
        
        this.totalItems++;
        //Logger.Instance.Debug(`📋 Registered video for loading: ${url}`, 'MaterialLoading');
        
        // Setup video loading events
        this.setupVideoEvents(url, videoElement);
        this.updateProgress();
    }
    
    private setupVideoEvents(url: string, videoElement: HTMLVideoElement): void {
        const item = this.loadingItems.get(url);
        if (!item) return;
        
        const onCanPlay = () => {
            //Logger.Instance.Debug(`🎬 Video can play: ${url}`, 'MaterialLoading');
            item.isLoaded = true;
            item.progress = 100;
            this.loadedItems++;
            
            // 🔍 DEBUG: Mark completion with debugger  
            const dimensions = videoElement.videoWidth && videoElement.videoHeight ? 
                { width: videoElement.videoWidth, height: videoElement.videoHeight } : undefined;
            MaterialLoadingDebugger.Instance.markCompleted(url, dimensions);
            
            this.updateProgress();
            this.checkIfAllLoaded();
            
            // Cleanup
            videoElement.removeEventListener('canplaythrough', onCanPlay);
            videoElement.removeEventListener('error', onError);
        };
        
        const onError = () => {
            Logger.Instance.Error(`❌ Video failed to load: ${url}`, 'MaterialLoading');
            item.hasError = true;
            this.failedItems++;
            this.updateProgress();
            
            // Cleanup
            videoElement.removeEventListener('canplaythrough', onCanPlay);
            videoElement.removeEventListener('error', onError);
        };
        
        const onProgress = () => {
            if (videoElement.buffered.length > 0) {
                const bufferedEnd = videoElement.buffered.end(0);
                const duration = videoElement.duration;
                if (duration > 0) {
                    item.progress = (bufferedEnd / duration) * 100;
                    this.updateProgress();
                }
            }
        };
        
        videoElement.addEventListener('canplaythrough', onCanPlay);
        videoElement.addEventListener('error', onError);
        videoElement.addEventListener('progress', onProgress);
        
        // If video is already ready
        if (videoElement.readyState >= 4) {
            onCanPlay();
        }
    }
    
    /**
     * Get THREE.js LoadingManager for texture loading
     */
    public getThreeLoadingManager(): THREE.LoadingManager {
        return this.threeLoadingManager;
    }
    
    /**
     * Get a TextureLoader configured with the global loading manager
     */
    public getTextureLoader(): THREE.TextureLoader {
        return new THREE.TextureLoader(this.threeLoadingManager);
    }
    
    /**
     * Check if using the global loading manager
     */
    public isUsingGlobalLoader(): boolean {
        return this.threeLoadingManager !== null;
    }
    
    /**
     * Get debug information about loading state
     */
    public getDebugInfo(): {
        totalItems: number;
        loadedCount: number;
        failedCount: number;
        hangingCount: number;
        loadedItems: Array<{type: string, url: string}>;
        failedItems: Array<{type: string, url: string}>;
        hangingItems: Array<{type: string, url: string, timeElapsed: number}>;
    } {
        const loadedItems: Array<{type: string, url: string}> = [];
        const failedItems: Array<{type: string, url: string}> = [];
        const hangingItems: Array<{type: string, url: string, timeElapsed: number}> = [];
        
        for (const item of this.loadingItems.values()) {
            if (item.isLoaded) {
                loadedItems.push({ type: item.type, url: item.url });
            } else if (item.hasError) {
                failedItems.push({ type: item.type, url: item.url });
            } else {
                const timeElapsed = (performance.now() - this.getItemStartTime(item.url)) / 1000;
                hangingItems.push({ type: item.type, url: item.url, timeElapsed });
            }
        }
        
        return {
            totalItems: this.totalItems,
            loadedCount: this.loadedItems,
            failedCount: this.failedItems,
            hangingCount: hangingItems.length,
            loadedItems,
            failedItems,
            hangingItems
        };
    }
    
    /**
     * Mark a texture as loaded (called by THREE.js loader)
     */
    public markTextureLoaded(url: string): void {
        const item = this.loadingItems.get(url);
        if (item && !item.isLoaded) {
            item.isLoaded = true;
            item.progress = 100;
            this.loadedItems++;
            
            // 🔍 DEBUG: Mark completion with debugger
            MaterialLoadingDebugger.Instance.markCompleted(url);
            
            //Logger.Instance.Debug(`✅ Texture loaded: ${url}`, 'MaterialLoading');
            this.updateProgress();
            this.checkIfAllLoaded();
        }
    }
    
    /**
     * Get current loading progress
     */
    public getProgress(): MaterialLoadingProgress {
        const currentlyLoading = Array.from(this.loadingItems.values())
            .filter(item => !item.isLoaded && !item.hasError)
            .map(item => item.url);
        
        const progress = this.totalItems > 0 ? 
            ((this.loadedItems + this.failedItems) / this.totalItems) * 100 : 100;
        
        return {
            totalMaterials: this.totalItems,
            loadedMaterials: this.loadedItems,
            failedMaterials: this.failedItems,
            currentlyLoading,
            progress: Math.round(progress)
        };
    }
    
    /**
     * Wait until all materials are loaded
     */
    public async waitUntilAllLoaded(): Promise<void> {
        if (this.isAllLoaded()) {
            return Promise.resolve();
        }
        return this.allLoadedPromise;
    }
    
    /**
     * Check if all materials are loaded
     */
    public isAllLoaded(): boolean {
        // If no materials were registered, consider loading complete
        if (this.totalItems === 0) {
            return true;
        }
        
        // Otherwise, check if all registered items are loaded or failed
        return (this.loadedItems + this.failedItems) >= this.totalItems;
    }
    
    private updateProgress(): void {
        const progress = this.getProgress();
        this.eventManager.emit('material-loading:progress', progress);
        
        //Logger.Instance.Debug(
        //    `📊 Material loading progress: ${progress.progress}% (${progress.loadedMaterials}/${progress.totalMaterials})`,
        //    'MaterialLoading'
        //);
    }
    
    private checkIfAllLoaded(): void {
        if (this.isAllLoaded()) {
            //Logger.Instance.Info(
            //    `🎉 All materials loaded! Success: ${this.loadedItems}, Failed: ${this.failedItems}`,
            //    'MaterialLoading'
             //);
            
            // 🎯 Stop checking for hanging materials since all loading is complete
            if (this.hangingCheckInterval) {
                clearInterval(this.hangingCheckInterval);
                this.hangingCheckInterval = null;
                //Logger.Instance.Debug('✅ Stopped hanging materials check - all loading complete', 'MaterialLoading');
            }
            
            // 🔍 DEBUG: Generate performance report when all materials are loaded
            MaterialLoadingDebugger.Instance.generatePerformanceReport();
            
            this.eventManager.emit('material-loading:complete', this.getProgress());
            
            // Resolve the promise
            if (this.allLoadedResolve) {
                this.allLoadedResolve();
                this.allLoadedResolve = null;
            }
        }
    }
    
    /**
     * Reset loading state
     */
    public reset(): void {
        // Clear any existing hanging check interval and restart it
        if (this.hangingCheckInterval) {
            clearInterval(this.hangingCheckInterval);
        }
        
        this.loadingItems.clear();
        this.totalItems = 0;
        this.loadedItems = 0;
        this.failedItems = 0;
        this.itemStartTimes.clear();
        
        // Create new promise
        this.allLoadedPromise = new Promise<void>((resolve) => {
            this.allLoadedResolve = resolve;
        });
        
        // Restart hanging check interval for new loading session
        this.setupLoadingTimeout();
        
        // If no materials will be registered, we should complete immediately
        // This will be triggered by checkIfAllLoaded() after registration phase
    }
    
    /**
     * Call this after the registration phase is complete to check if loading should finish immediately
     */
    public finalizeRegistration(): void {
        //Logger.Instance.Debug(`📋 Material registration finalized - ${this.totalItems} materials registered`, 'MaterialLoading');
        
        // If no materials were registered, complete immediately
        if (this.totalItems === 0) {
            //Logger.Instance.Info('✅ No materials to load - completing immediately', 'MaterialLoading');
            this.checkIfAllLoaded();
        }
    }
    
    /**
     * Force complete loading by marking hanging items as failed
     */
    public forceComplete(): void {
        Logger.Instance.Warn('🚫 Force completing material loading', 'MaterialLoading');
        
        const hangingItems = Array.from(this.loadingItems.values())
            .filter(item => !item.isLoaded && !item.hasError);
        
        Logger.Instance.Warn(`⚠️ Marking ${hangingItems.length} hanging items as failed:`, 'MaterialLoading');
        
        hangingItems.forEach(item => {
            Logger.Instance.Warn(`  ❌ Failed: ${item.type} - ${item.url}`, 'MaterialLoading');
            item.hasError = true;
            this.failedItems++;
        });
        
        this.updateProgress();
        this.checkIfAllLoaded();
    }
    
    /**
     * Dispose resources
     */
    public dispose(): void {
        // Clear hanging check interval
        if (this.hangingCheckInterval) {
            clearInterval(this.hangingCheckInterval);
            this.hangingCheckInterval = null;
        }
        
        this.loadingItems.clear();
        this.itemStartTimes.clear();
        this.eventManager.removeAllListeners('material-loading:*');
    }
} 